import { SpotOrderRecord, SpotOrderStatus } from 'api/v1/spot'
import { MobileUserOrderModal } from 'components/MobileUserOrderModalV3'
import { CSSProperties, memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useCancelAllSpotState,
  useCancelSpotPendingIds,
  useSpotSymbols,
} from 'store/spot/hooks'
import { HistoryOrdersWrap, SymbolLink, TriggerDone } from '../styles'
import { TableList } from 'components/TableList'
import { DateFormatter } from 'utils/datetime'
import { ReactComponent as ArrowIconSvg } from 'assets/orders/symbol_arrow.svg'
import { useMarketNavigate } from 'hooks/useMarketNavigate'
import { findSymbolById } from 'utils/symbols'
import { TrendColorizer } from 'components/TrendColorizer'
import {
  OrderDirection,
  OrderType,
  SpotOrderDirectionFilterEnum,
} from 'store/spot/types'
import { isZero, toFixed } from 'utils/number'
import numbro from 'numbro'
import { calcOrderFilledSize, calcOrderValue } from 'hooks/useSizeCalculations'
import Loading from 'components/Loading'
import { FlexEnd } from 'styles'
import { CancelText, OpenCancel } from './styles'
import { SideSelectPopup } from '../components/SideSelectPopup'
import { getStopOrderType } from 'utils/getStopOrderTriggerDirection'
import { isMobile } from '../../../../utils/userAgent'
import { StringFormatter } from '../../../../utils/StringFormatter'
import { SpotCancelModal } from '../../CancelModal'
import { SpotCancelAll } from '../Top/CancelAll'
import MobileTableList from 'components/TableList/MobileTableList'
import MobileRowItem from './MobileRowItem'
import { useCancelOrder } from 'hooks/spot/useCancelOrder'

export const SpotOpenOrderList = memo<{
  rows: SpotOrderRecord[]
  tableBodyStyle?: CSSProperties
}>(({ rows, tableBodyStyle }) => {
  const { t } = useTranslation()
  const cancelSpotPendingIds = useCancelSpotPendingIds()
  const { navigateToSpot } = useMarketNavigate()
  const [filterSide, setFilterSide] = useState(SpotOrderDirectionFilterEnum.All)

  const spotSymbols = useSpotSymbols()
  const cancelAllState = useCancelAllSpotState()

  const cancelOrder = useCancelOrder()

  const getSymbolInfo = (symbolId: number) => {
    const symbol = findSymbolById(spotSymbols, symbolId)
    const {
      baseCurrency = '',
      quoteCurrency = '',
      baseScale = 0,
      quoteScale = 0,
    } = symbol ?? {}

    return {
      baseCurrency,
      quoteCurrency,
      baseScale,
      quoteScale,
    }
  }

  const getFixedValue = (row: SpotOrderRecord) => {
    const {
      type = OrderType.LIMIT,
      price = 0,
      triggerOn = '',
      quantity,
      symbolId,
    } = row
    const isStopOrder = !isZero(triggerOn)
    const isStopMarket = type === OrderType.MARKET && isStopOrder
    const symbol = getSymbolInfo(symbolId)
    const { quoteScale } = symbol
    const priceOrTriggerPrice = isStopMarket ? Number(triggerOn) : price
    const value = calcOrderValue(String(quantity), priceOrTriggerPrice)
    const fixedValue = toFixed(value, quoteScale)
    return fixedValue
  }

  const getFilledSizeQuantity = (row: SpotOrderRecord) => {
    const { symbolId, quantity = 0, unfilledQuantity = 0 } = row
    const symbol = getSymbolInfo(symbolId)
    const { baseScale } = symbol
    const filledSize = calcOrderFilledSize(quantity, unfilledQuantity)
    const fixedQuantity = toFixed(quantity, baseScale)
    return {
      filledSize,
      fixedQuantity,
    }
  }

  const filterRenderList = useMemo(() => {
    if (rows?.length > 0) {
      let list = rows

      if (filterSide !== SpotOrderDirectionFilterEnum.All) {
        // @ts-ignore
        list = list.filter((d) => d.direction === filterSide)
      }
      return list
    }
    return rows
  }, [rows, filterSide])

  const onSelectDirectionFilter = (value: SpotOrderDirectionFilterEnum) => {
    setFilterSide(value)
  }

  const columns = [
    {
      key: 'date',
      title: 'Date',
      render: (row: SpotOrderRecord) => {
        const { createdAt } = row
        const datetime = DateFormatter.toISODateTime(createdAt)
        return <span style={{ color: '#fff' }}>{datetime}</span>
      },
      width: '13%',
    },
    {
      key: 'symbol',
      title: 'Pair',
      // title: t('user-order-table-header-market'),
      render: (row: SpotOrderRecord) => {
        const { symbolId } = row
        const symbol = getSymbolInfo(symbolId)
        const { baseCurrency, quoteCurrency } = symbol ?? {}
        return (
          <SymbolLink
            sx={{ cursor: 'pointer' }}
            onClick={() => navigateToSpot(baseCurrency, quoteCurrency)}
          >
            {baseCurrency}/{quoteCurrency}
            <ArrowIconSvg />
          </SymbolLink>
        )
      },
      width: '12%',
    },
    {
      key: 'type',
      title: t('user-order-table-header-type'),
      width: '10%',
      render: (row: SpotOrderRecord) => {
        const { triggerOn, type, triggerDirection, direction } = row
        const isStopOrder = !isZero(triggerOn)
        const orderType = isStopOrder
          ? getStopOrderType(type, direction, triggerDirection)
          : StringFormatter.startCase(type)
        return <>{orderType}</>
      },
    },
    {
      key: 'side',
      // title: t('user-order-table-header-side'),
      titleRender: () => {
        return (
          <SideSelectPopup
            onSelected={onSelectDirectionFilter}
            filterSide={filterSide}
          />
        )
      },
      render: (row: SpotOrderRecord) => {
        const { direction } = row
        return (
          <TrendColorizer
            trend={direction === OrderDirection.LONG}
            value={
              direction === OrderDirection.LONG
                ? t('user-order-table-content-buy')
                : t('user-order-table-content-sell')
            }
          />
        )
      },
      width: '8%',
    },
    {
      key: 'size',
      title: t('user-order-table-header-size'),
      render: (row: SpotOrderRecord) => {
        const { quantity, symbolId } = row
        const symbol = getSymbolInfo(symbolId)
        const { baseScale, baseCurrency } = symbol
        const fixedQuantity = toFixed(quantity, baseScale)
        return `${fixedQuantity} ${baseCurrency}`
      },
      width: '10%',
    },
    {
      key: 'size-price',
      title: t('user-order-table-header-price'),
      render: (row: SpotOrderRecord) => {
        const { triggerOn, symbolId, type = OrderType.LIMIT, price = 0 } = row
        const symbol = getSymbolInfo(symbolId)
        const { quoteScale, quoteCurrency } = symbol
        const isStopOrder = !isZero(triggerOn)
        const isStopMarket = type === OrderType.MARKET && isStopOrder
        const priceOrTriggerPrice = isStopMarket ? Number(triggerOn) : price
        const fixedPrice = toFixed(priceOrTriggerPrice, quoteScale)

        return isStopMarket ? 'Market' : `${fixedPrice} ${quoteCurrency}`
      },
      width: '11%',
    },
    {
      key: 'Filled',
      title: t('user-order-table-header-filled'),
      render: (row: SpotOrderRecord) => {
        const { quantity = 0 } = row
        const { filledSize, fixedQuantity } = getFilledSizeQuantity(row)
        return (
          <>
            <span>{filledSize}</span>
            <span>/{fixedQuantity}</span>
            <span>
              (
              {numbro(Number(filledSize) / Number(quantity)).format({
                output: 'percent',
                mantissa: 2,
              })}
              )
            </span>
          </>
        )
      },
      width: '12%',
    },
    {
      key: 'Value',
      title: 'Total',
      // title: t('user-order-table-header-value'),
      render: (row: SpotOrderRecord) => {
        const { symbolId } = row
        const fixedValue = getFixedValue(row)
        const { quoteCurrency } = getSymbolInfo(symbolId)
        return <>{`${fixedValue} ${quoteCurrency}`}</>
      },
      width: '12%',
    },
    {
      key: 'Trigger Conditions',
      title: t('user-order-table-header-trigger'),
      render: (row: SpotOrderRecord) => {
        const {
          triggerOn,
          triggerDirection = OrderDirection.LONG,
          status,
        } = row
        return (
          <>
            {isZero(triggerOn) ? (
              '-'
            ) : (
              <>
                Last Price{' '}
                {triggerDirection === OrderDirection.LONG ? '>=' : '<='}{' '}
                {triggerOn}
                {status === SpotOrderStatus.PENDING && <TriggerDone />}
              </>
            )}
          </>
        )
      },
      width: '12%',
    },
    {
      key: 'operate',
      titleRender: () => {
        return <SpotCancelAll />
      },
      render: (row: SpotOrderRecord) => {
        const { id } = row
        const isExist = cancelSpotPendingIds?.find((item) => item === id)
        return (
          <FlexEnd style={{ width: '100%' }}>
            <OpenCancel
              className={isExist || cancelAllState ? 'disabled' : ''}
              onClick={() => {
                if (isExist || cancelAllState) return
                cancelOrder(row)
              }}
            >
              {isExist ? <Loading /> : <CancelText>Cancel</CancelText>}
            </OpenCancel>
          </FlexEnd>
        )
      },
      width: '8%',
    },
  ]

  return (
    <HistoryOrdersWrap>
      {isMobile ? (
        <MobileTableList
          dataSource={filterRenderList}
          renderItem={(row) => {
            return <MobileRowItem row={row}></MobileRowItem>
          }}
        />
      ) : (
        <TableList
          columns={columns}
          dataSource={filterRenderList}
          tableBodyStyle={tableBodyStyle}
        />
      )}

      <MobileUserOrderModal openOrderCancel={cancelOrder as any} />

      <SpotCancelModal />
    </HistoryOrdersWrap>
  )
})
